import { FilePlus, Upload, X } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { Badge } from "@/shared/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  DocumentDraft,
  TAX_DOCUMENT_TYPE_OPTIONS,
} from "./utils";

interface TaxDocumentFormProps {
  draft: DocumentDraft;
  onDraftChange: (draft: DocumentDraft) => void;
  onAdd: () => void;
  error?: string | null;
  disabled?: boolean;
  documentsCount?: number;
  fileInputRef: React.RefObject<HTMLInputElement>;
  acceptedTypes: string;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFileButtonClick: () => void;
  onFileRemove: () => void;
}

export function TaxDocumentForm({
  draft,
  onDraftChange,
  onAdd,
  error,
  disabled = false,
  documentsCount = 0,
  fileInputRef,
  acceptedTypes,
  onFileChange,
  onFileButtonClick,
  onFileRemove,
}: TaxDocumentFormProps) {
  const canAdd = draft.documentType && draft.file;

  return (
    <div className="space-y-4 rounded-lg border border-white/10 bg-muted/15 p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Anexos da taxa (opcional)</h3>
          <p className="text-xs text-muted-foreground">
            Adicione documentos comprobatórios relacionados à taxa.
          </p>
        </div>
        {documentsCount > 0 && (
          <Badge variant="outline" className="w-fit">
            {documentsCount} documento{documentsCount !== 1 ? "s" : ""} anexado
            {documentsCount !== 1 ? "s" : ""}
          </Badge>
        )}
      </div>

      {documentsCount === 0 && !draft.file && (
        <div className="rounded-md border border-dashed border-muted-foreground/40 bg-background/20 p-4">
          <div className="flex flex-col items-center justify-center gap-3 text-center">
            <FilePlus className="h-8 w-8 text-muted-foreground" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">Nenhum documento anexado</p>
              <p className="text-xs text-muted-foreground">
                Você pode adicionar documentos comprobatórios se necessário
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="tax-document-type">Tipo do documento</Label>
            <Select
              value={draft.documentType}
              onValueChange={(value) =>
                onDraftChange({ ...draft, documentType: value as typeof draft.documentType })
              }
              disabled={disabled}
            >
              <SelectTrigger id="tax-document-type">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                {TAX_DOCUMENT_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Arquivo</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedTypes}
              onChange={onFileChange}
              disabled={disabled}
              className="hidden"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onFileButtonClick}
                disabled={disabled}
                className="w-full sm:w-auto"
              >
                <Upload className="mr-2 h-4 w-4" />
                {draft.file ? "Trocar arquivo" : "Selecionar arquivo"}
              </Button>
              {draft.file && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={onFileRemove}
                  disabled={disabled}
                >
                  <X className="mr-1 h-3 w-3" /> Remover
                </Button>
              )}
            </div>
            {draft.file && (
              <div className="rounded-md bg-muted/30 p-2">
                <p className="truncate text-xs font-medium text-foreground">{draft.file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(draft.file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tax-document-description">Descrição (opcional)</Label>
          <Textarea
            id="tax-document-description"
            placeholder="Notas adicionais sobre o documento"
            value={draft.description}
            onChange={(event) => onDraftChange({ ...draft, description: event.target.value })}
            disabled={disabled}
            rows={2}
          />
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        {canAdd && (
          <Button
            type="button"
            variant="default"
            size="sm"
            className="w-full"
            onClick={onAdd}
            disabled={disabled}
          >
            <FilePlus className="mr-2 h-4 w-4" /> Adicionar à lista
          </Button>
        )}
      </div>
    </div>
  );
}

